import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Search, Download, Upload, Trash2, Phone, Globe, Mail, Star, Filter, X, Plus, Sparkles, Database, MessageCircle, ShieldCheck, ShieldX, Lock, Target, CheckCircle2, AlertCircle } from "lucide-react";
import api, { apiErr, API } from "@/lib/api";
import { Button, Input, Card, Badge, Spinner, STATUS_COLORS, TAG_COLORS } from "@/components/kit";
import SearchSelect from "@/components/SearchSelect";
import Pagination from "@/components/Pagination";

const STATUSES = ["new", "contacted", "replied", "negotiating", "closed", "lost"];
const TAGS = ["hot", "warm", "cold", "irrelevant"];
const TIERS = [{ value: "hot", label: "🔥 Hot", color: "red" }, { value: "warm", label: "🌤 Warm", color: "amber" }, { value: "cold", label: "❄️ Cold", color: "sky" }];
const EMPTY_MANUAL = { business_name: "", phone: "", email: "", website: "", address: "", city: "", category: "", status: "new", tag: "warm", notes: "" };

export default function Leads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: "", status: "", tag: "", city: "", no_website: false, has_email: false });
  const [facets, setFacets] = useState({ cities: [], categories: [] });
  const [selected, setSelected] = useState({});
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [savingManual, setSavingManual] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [waChecking, setWaChecking] = useState(false);
  const [waAccount, setWaAccount] = useState(null);
  const [aiDetail, setAiDetail] = useState(null);
  const [tierFilter, setTierFilter] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [scoreSummary, setScoreSummary] = useState(null);
  const importRef = useRef(null);

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/leads/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview({ ...data, file: f });
    } catch (err) { toast.error(apiErr(err)); }
    finally { if (importRef.current) importRef.current.value = ""; }
  };

  const confirmImport = async () => {
    if (!importPreview?.file) return;
    const fd = new FormData();
    fd.append("file", importPreview.file);
    try {
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} lead diimpor${data.skipped ? `, ${data.skipped} duplikat` : ""}${data.invalid ? `, ${data.invalid} dilewati` : ""}`);
      setImportPreview(null); setPage(1); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20, ...filters };
      if (tierFilter) params.tier = tierFilter;
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] === false) && delete params[k]);
      const { data } = await api.get("/leads", { params });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [page, filters, tierFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/leads/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/leads/score-summary").then((r) => setScoreSummary(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/wa/account").then((r) => setWaAccount(r.data)).catch(() => setWaAccount({ connected: false })); }, []);

  const canValidate = !!(waAccount?.connected && waAccount?.device?.can_validate);
  const waBlockReason = !waAccount?.connected
    ? "Hubungkan akun Fonnte (WA Devices) dulu untuk Cek WA."
    : !waAccount?.device?.can_validate
      ? `Cek WA butuh paket Fonnte premium. Paket sekarang: ${waAccount?.device?.package || "—"}.`
      : "";

  const updateLead = async (id, patch) => {
    try {
      await api.put(`/leads/${id}`, patch);
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const bulk = async (action, value) => {
    try {
      await api.post("/leads/bulk-action", { lead_ids: selectedIds, action, value });
      toast.success("Berhasil diperbarui");
      setSelected({});
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("ls_token");
    fetch(`${API}/leads/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "leads.csv"; a.click();
      });
  };

  const enrichSelected = async () => {
    if (selectedIds.length === 0) return;
    setEnriching(true);
    try {
      const { data: res } = await api.post("/leads/enrich", { lead_ids: selectedIds });
      setData((d) => ({
        ...d,
        leads: d.leads.map((l) => res.results[l.id]
          ? { ...l, ai_enriched: true, ai_description: res.results[l.id].description, ai_segment: res.results[l.id].segment, ai_scale: res.results[l.id].scale, ai_products: res.results[l.id].products }
          : l),
      }));
      toast.success(`${res.enriched} lead dianalisa AI${res.failed ? `, ${res.failed} gagal` : ""}`);
      setSelected({});
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEnriching(false); }
  };

  const checkWaSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!canValidate) { toast.error(waBlockReason); return; }
    setWaChecking(true);
    try {
      const { data: res } = await api.post("/leads/check-wa", { numbers: selectedIds });
      const map = res.results || {};
      setData((d) => ({
        ...d,
        leads: d.leads.map((l) => {
          const v = map[l.id];
          if (v === true || v === false) return { ...l, wa_ready: v };
          return l;
        }),
      }));
      const ok = Object.values(map).filter((v) => v === true).length;
      const no = Object.values(map).filter((v) => v === false).length;
      toast.success(`${ok} terdaftar · ${no} tidak (dari ${res.checked} dicek)`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setWaChecking(false); }
  };

  const scoreSelected = async () => {
    if (selectedIds.length === 0) { toast.error("Pilih lead terlebih dahulu"); return; }
    setScoring(true);
    try {
      const { data: res } = await api.post("/leads/score", { lead_ids: selectedIds });
      setData((d) => ({
        ...d,
        leads: d.leads.map((l) => res.results[l.id]
          ? { ...l, lead_score: res.results[l.id].score, lead_tier: res.results[l.id].tier, lead_signals: res.results[l.id].signals }
          : l),
      }));
      toast.success(`${res.scored} lead dianalisa skornya`);
      setSelected({});
      api.get("/leads/score-summary").then((r) => setScoreSummary(r.data)).catch(() => {});
    } catch (e) { toast.error(apiErr(e)); }
    finally { setScoring(false); }
  };
  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const totalPages = data ? Math.ceil(data.total / data.per_page) : 1;

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manual.business_name.trim()) { toast.error("Nama bisnis wajib diisi"); return; }
    setSavingManual(true);
    try {
      await api.post("/leads/manual", manual);
      toast.success("Lead manual ditambahkan");
      setShowManual(false); setManual(EMPTY_MANUAL);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingManual(false); }
  };
  const setM = (k) => (e) => setManual((m) => ({ ...m, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-3"><span className="w-10 h-10 rounded-xl bg-sky-100 grid place-items-center shrink-0"><Database className="w-5 h-5 text-sky-600" /></span><h1 className="font-display text-3xl font-extrabold">Leads</h1></span>
          <p className="text-slate-500 mt-1">{data?.total ?? 0} lead tersimpan di database kamu.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-manual-lead-btn" onClick={() => setShowManual(true)}><Plus className="w-4 h-4" /> Tambah Manual</Button>
          <input ref={importRef} type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" data-testid="leads-import-input" />
          <Button variant="outline" onClick={() => importRef.current?.click()} data-testid="leads-import"><Upload className="w-4 h-4" /> Import CSV/Excel</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="leads-export"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            <Input data-testid="leads-search" placeholder="Cari bisnis / alamat..." value={filters.search}
              onChange={(e) => setF("search", e.target.value)} className="pl-10 py-2" />
          </div>
          <select value={filters.status} onChange={(e) => setF("status", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize">
            <option value="">Semua Status</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.tag} onChange={(e) => setF("tag", e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize">
            <option value="">Semua Tag</option>{TAGS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <SearchSelect testid="leads-city-filter" options={facets.cities} value={filters.city}
            onChange={(v) => setF("city", v)} placeholder="Semua Lokasi" allLabel="Semua Lokasi" />
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={filters.no_website} onChange={(e) => setF("no_website", e.target.checked)} /> No website</label>
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={filters.has_email} onChange={(e) => setF("has_email", e.target.checked)} /> Punya email</label>
          <select value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="leads-tier-filter">
            <option value="">Semua Skor</option>
            {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {scoreSummary && scoreSummary.total > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Target className="w-3.5 h-3.5" />
              <span className="text-red-500 font-semibold">{scoreSummary.distribution.hot}</span>·
              <span className="text-amber-500 font-semibold">{scoreSummary.distribution.warm}</span>·
              <span className="text-sky-500 font-semibold">{scoreSummary.distribution.cold}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Bulk bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-sky-500 text-white rounded-xl px-4 py-2.5 sticky top-20 z-10">
          <span className="text-sm font-semibold">{selectedIds.length} dipilih</span>
          <select onChange={(e) => e.target.value && bulk("status", e.target.value)} defaultValue="" className="text-slate-700 rounded-lg px-2 py-1 text-sm">
            <option value="">Ubah status...</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select onChange={(e) => e.target.value && bulk("tag", e.target.value)} defaultValue="" className="text-slate-700 rounded-lg px-2 py-1 text-sm">
            <option value="">Ubah tag...</option>{TAGS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={enrichSelected} disabled={enriching} data-testid="enrich-ai-btn"
            className="flex items-center gap-1 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-2.5 py-1 disabled:opacity-60">
            <Sparkles className="w-4 h-4" /> {enriching ? "Menganalisa..." : "Enrich AI"}
          </button>
          <button onClick={checkWaSelected} disabled={waChecking || !canValidate} data-testid="leads-check-wa-btn"
            title={canValidate ? "Cek WA untuk lead terpilih" : waBlockReason}
            className="flex items-center gap-1 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-2.5 py-1 disabled:opacity-50">
            {canValidate ? <MessageCircle className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {waChecking ? "Cek WA..." : "Cek WA"}
          </button>
          <button onClick={scoreSelected} disabled={scoring} data-testid="leads-score-btn"
            className="flex items-center gap-1 text-sm font-semibold bg-white/20 hover:bg-white/30 rounded-lg px-2.5 py-1 disabled:opacity-60">
            <Target className="w-4 h-4" /> {scoring ? "Menghitung..." : "Score"}
          </button>
          <button onClick={() => bulk("delete")} className="ml-auto flex items-center gap-1 text-sm hover:underline"><Trash2 className="w-4 h-4" /> Hapus</button>
          <button onClick={() => setSelected({})}><X className="w-4 h-4" /></button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div> :
        data?.leads.length === 0 ? (
          <Card className="p-12 text-center text-slate-400">Belum ada lead. Mulai dari halaman Scraper.</Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="p-3 w-12">
                    <input type="checkbox"
                      checked={data.leads.length > 0 && data.leads.every((l) => selected[l.id])}
                      onChange={(e) => {
                        const v = e.target.checked;
                        const next = { ...selected };
                        data.leads.forEach((l) => (next[l.id] = v));
                        setSelected(next);
                      }}
                      className="w-5 h-5 accent-sky-500 cursor-pointer"
                      data-testid="leads-select-page"
                    />
                  </th>
                  <th className="p-3">Bisnis</th>
                  <th className="p-3 hidden md:table-cell">Kontak</th>
                  <th className="p-3 text-center">WA</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Tag</th>
                  <th className="p-3 text-center hidden md:table-cell">Skor</th>
                </tr>
              </thead>
              <tbody>
                {data.leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-50 hover:bg-slate-50/50" data-testid="lead-row">
                    <td className="p-3">
                      <input type="checkbox" checked={!!selected[l.id]} onChange={() => setSelected((s) => ({ ...s, [l.id]: !s[l.id] }))}
                        className="w-5 h-5 accent-sky-500 cursor-pointer" />
                    </td>
                    <td className="p-3">
                      <p className="font-semibold text-slate-800">{l.business_name}</p>
                      <p className="text-xs text-slate-400">{l.city} · <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400" fill="currentColor" />{l.rating}</span> · {l.category}</p>
                      {l.ai_enriched && (
                        <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="lead-ai-tags">
                          <Badge color="violet">{l.ai_segment}</Badge>
                          <Badge color="sky">{l.ai_scale}</Badge>
                          {(l.ai_products || []).slice(0, 3).map((p) => (
                            <span key={p} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{p}</span>
                          ))}
                          <button onClick={() => setAiDetail(l)} data-testid="lead-ai-detail-btn" className="text-[11px] text-violet-600 font-semibold inline-flex items-center gap-0.5 hover:underline"><Sparkles className="w-3 h-3" /> Detail</button>
                        </div>
                      )}
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div className="flex flex-col gap-0.5 text-xs text-slate-600">
                        {l.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-sky-500" />{l.phone}</span>}
                        {l.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3 text-sky-500" />web</span>}
                        {l.email && <span className="flex items-center gap-1 truncate max-w-[180px]" title={l.email}><Mail className="w-3 h-3 text-violet-500" /><span className="truncate">{l.email}</span></span>}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {!l.phone ? <span className="text-slate-300 text-xs">—</span>
                        : l.wa_ready === true ? <Badge color="green"><ShieldCheck className="w-3 h-3" /> Ready</Badge>
                        : l.wa_ready === false ? <Badge color="red"><ShieldX className="w-3 h-3" /> Tidak</Badge>
                        : <span className="text-slate-300 text-xs">?</span>}
                    </td>
                    <td className="p-3">
                      <select value={l.status} onChange={(e) => updateLead(l.id, { status: e.target.value })}
                        className="rounded-lg px-2 py-1 text-xs font-semibold capitalize border border-slate-200 cursor-pointer">
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3">
                      <select value={l.tag} onChange={(e) => updateLead(l.id, { tag: e.target.value })}
                        className="rounded-lg px-2 py-1 text-xs font-semibold capitalize border border-slate-200 cursor-pointer">
                        {TAGS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="p-3 text-center hidden md:table-cell">
                      {l.lead_score != null ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className={`text-sm font-bold ${l.lead_tier === "hot" ? "text-red-500" : l.lead_tier === "warm" ? "text-amber-500" : "text-sky-500"}`}>
                            {l.lead_score}
                          </span>
                          <Badge color={l.lead_tier === "hot" ? "red" : l.lead_tier === "warm" ? "amber" : "sky"} className="text-[10px]">
                            {l.lead_tier}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

      {data && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}

      {showManual && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowManual(false)} />
          <Card className="relative w-full max-w-lg p-6 animate-float-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">Tambah Lead Manual</h2>
              <button onClick={() => setShowManual(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={submitManual} className="space-y-3">
              <div>
                <label className="text-sm font-semibold text-slate-700">Nama Bisnis *</label>
                <Input data-testid="manual-business-name" required placeholder="Restoran Sederhana" value={manual.business_name} onChange={setM("business_name")} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Telepon</label>
                  <Input data-testid="manual-phone" placeholder="08123456789" value={manual.phone} onChange={setM("phone")} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Email</label>
                  <Input data-testid="manual-email" placeholder="info@bisnis.com" value={manual.email} onChange={setM("email")} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Website</label>
                  <Input placeholder="bisnis.co.id" value={manual.website} onChange={setM("website")} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Kategori</label>
                  <Input placeholder="Restoran" value={manual.category} onChange={setM("category")} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Kota</label>
                  <Input placeholder="Jakarta" value={manual.city} onChange={setM("city")} className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Alamat</label>
                  <Input placeholder="Jl. ..." value={manual.address} onChange={setM("address")} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Status</label>
                  <select value={manual.status} onChange={setM("status")} className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm capitalize">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700">Tag</label>
                  <select value={manual.tag} onChange={setM("tag")} className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm capitalize">
                    {TAGS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Catatan</label>
                <textarea placeholder="Catatan sales..." value={manual.notes} onChange={setM("notes")} rows={2}
                  className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" />
              </div>
              <Button data-testid="manual-submit" type="submit" loading={savingManual} className="w-full">Simpan Lead</Button>
            </form>
          </Card>
        </div>
      )}
      {aiDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ai-detail-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setAiDetail(null)} />
          <Card className="relative w-full max-w-md p-6 animate-float-up">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" /> Analisa AI</h2>
              <button onClick={() => setAiDetail(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <p className="font-semibold text-slate-800">{aiDetail.business_name}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge color="violet">{aiDetail.ai_segment}</Badge>
              <Badge color="sky">Skala {aiDetail.ai_scale}</Badge>
            </div>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">{aiDetail.ai_description}</p>
            {(aiDetail.ai_products || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Produk / Layanan</p>
                <div className="flex flex-wrap gap-1.5">
                  {aiDetail.ai_products.map((p) => (
                    <span key={p} className="text-xs bg-violet-50 text-violet-700 rounded-full px-2.5 py-1">{p}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Import Preview Modal */}
      {importPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setImportPreview(null)} />
          <Card className="relative w-full max-w-2xl p-6 animate-float-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg flex items-center gap-2">
                <Upload className="w-5 h-5 text-sky-500" /> Preview Import
              </h2>
              <button onClick={() => setImportPreview(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <p className="font-display text-2xl font-extrabold text-emerald-600">{importPreview.valid}</p>
                <p className="text-xs text-emerald-700">Valid</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="font-display text-2xl font-extrabold text-amber-600">{importPreview.duplicates}</p>
                <p className="text-xs text-amber-700">Duplikat</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-3 text-center">
                <p className="font-display text-2xl font-extrabold text-rose-600">{importPreview.invalid}</p>
                <p className="text-xs text-rose-700">Invalid</p>
              </div>
            </div>

            {importPreview.preview?.length > 0 && (
              <div className="rounded-lg border border-slate-100 divide-y divide-slate-50 max-h-60 overflow-y-auto mb-4">
                {importPreview.preview.map((row, i) => (
                  <div key={i} className="px-3 py-2 text-xs flex items-center gap-2">
                    <span className="text-slate-400 w-8 shrink-0">#{row.row}</span>
                    <span className="font-semibold text-slate-700 truncate flex-1">{row.business_name || "—"}</span>
                    <span className="text-slate-500 w-24 truncate">{row.phone || row.email || "—"}</span>
                    {row.status === "valid" && <Badge color="green"><CheckCircle2 className="w-3 h-3" /> Valid</Badge>}
                    {row.status === "duplicate" && <Badge color="amber">Duplikat</Badge>}
                    {row.status === "invalid" && <Badge color="red"><AlertCircle className="w-3 h-3" /> {row.reason}</Badge>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between items-center">
              <p className="text-xs text-slate-400">{importPreview.total_rows} total baris</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setImportPreview(null)}>Batal</Button>
                <Button onClick={confirmImport} disabled={importPreview.valid === 0} data-testid="import-confirm">
                  <Upload className="w-4 h-4" /> Import {importPreview.valid} Lead
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
